import type { Serialiser, SerialiserResult } from "hooks/serialisers/types";
import { FLAG_TRANSLATION_KEY } from "components/heatQualityIssues/issueLabels";
import type { HeatQualityFlag } from "src/store/api/generatedApi";
import utc from "dayjs/plugin/utc";
import dayjs, { type Dayjs } from "dayjs";
import { z } from "zod";

dayjs.extend(utc);

const parsedSelection = <T>(selection: T | undefined): SerialiserResult<T> =>
  selection === undefined
    ? { valid: false }
    : { valid: true, value: selection };

export const jsonQuery = <T>(schema: z.ZodType<T>): Serialiser<T, never> => ({
  parse: (text) => {
    try {
      const parsed = schema.safeParse(JSON.parse(text));
      return parsed.success
        ? { valid: true, value: parsed.data }
        : { valid: false };
    } catch {
      return { valid: false };
    }
  },
  format: (selection) => JSON.stringify(selection),
});

export const textQuery: Serialiser<string, never> = {
  parse: (text) => ({ valid: true, value: text }),
  format: (text) => text,
};
export const optionalTextQuery: Serialiser<string | null, never> = {
  parse: (text) => ({ valid: true, value: text || null }),
  format: (text) => text ?? "",
};
export const choiceQuery = <T extends string>(
  choices: readonly T[]
): Serialiser<T, never> => ({
  parse: (text) => parsedSelection(choices.find((choice) => choice === text)),
  format: (choice) => choice,
});
export const toggleQuery: Serialiser<boolean, never> = {
  parse: (text) =>
    parsedSelection(text === "on" ? true : text === "off" ? false : undefined),
  format: (enabled) => (enabled ? "on" : "off"),
};
export const identifierQuery: Serialiser<number | null, never> = {
  parse: (text) =>
    parsedSelection(
      text === ""
        ? null
        : /^\d+$/.test(text) &&
            Number.isSafeInteger(Number(text)) &&
            Number(text) > 0
          ? Number(text)
          : undefined
    ),
  format: (identifier) => identifier?.toString() ?? "",
};
export const numberQuery = (
  choices: readonly number[]
): Serialiser<number, never> => ({
  parse: (text) =>
    parsedSelection(choices.find((choice) => String(choice) === text)),
  format: (number) => String(number),
});
export const stringListQuery = jsonQuery(z.array(z.string()));
export const identifierListQuery = jsonQuery(
  z.array(z.number().int().positive())
);
export const stringSetQuery: Serialiser<Set<string>, never> = {
  parse: (text) => {
    const strings = stringListQuery.parse(text);
    return strings.valid
      ? { valid: true, value: new Set(strings.value) }
      : strings;
  },
  format: (strings) => stringListQuery.format([...strings]),
};
export const timestampQuery: Serialiser<string, never> = {
  parse: (text) =>
    parsedSelection(
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z$/.test(text) &&
        dayjs.utc(text).isValid() &&
        dayjs.utc(text).format("YYYY-MM-DDTHH:mm:ss") === text.slice(0, 19)
        ? text
        : undefined
    ),
  format: (timestamp) => timestamp,
};
export const dateQuery: Serialiser<Dayjs, never> = {
  parse: (text) =>
    timestampQuery.parse(text).valid
      ? { valid: true, value: dayjs.utc(text) }
      : { valid: false },
  format: (date) => (date.isValid() ? date.toISOString() : ""),
};
export const dateRangeQuery: Serialiser<[Dayjs | null, Dayjs | null], never> = {
  parse: (text) => {
    const parts = stringListQuery.parse(text);
    if (!parts.valid || parts.value.length !== 2) return { valid: false };
    const [startText, endText] = parts.value;
    if (startText == null || endText == null) return { valid: false };
    const start = dateQuery.parse(startText);
    const end = dateQuery.parse(endText);
    return start.valid && end.valid && start.value.isBefore(end.value)
      ? { valid: true, value: [start.value, end.value] }
      : { valid: false };
  },
  format: ([start, end]) =>
    JSON.stringify([start?.toISOString(), end?.toISOString()]),
};
export const readonlyStringListQuery = jsonQuery(
  z.array(z.string()).readonly()
);
export const readonlyIdentifierListQuery = jsonQuery(
  z.array(z.number().int().positive()).readonly()
);
export const optionalStringListQuery = jsonQuery(
  z.array(z.string()).readonly().nullable()
);
export const datasetSplitsQuery = jsonQuery(
  z.array(z.enum(["train", "val", "test"]))
);
export const angleOverridesQuery = jsonQuery(z.record(z.string(), z.boolean()));
export const equipmentQuery = jsonQuery(
  z
    .object({
      kind: z.enum(["device", "sensor", "position"]),
      id: z.number().int().positive(),
    })
    .nullable()
);
export const nonnegativeNumberQuery: Serialiser<number, never> = {
  parse: (text) =>
    parsedSelection(
      /^\d+$/.test(text) && Number.isSafeInteger(Number(text))
        ? Number(text)
        : undefined
    ),
  format: (number) => String(number),
};

export const heatFlagsQuery = jsonQuery(
  z.array(z.enum(Object.keys(FLAG_TRANSLATION_KEY) as HeatQualityFlag[]))
);
